import { IoClose } from 'react-icons/io5';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@frontend/components/ui/avatar';
import { Separator } from '@frontend/components/ui/separator';
import { Answer } from '@frontend/shared/components/Answer';
import { QuestionActions } from '@frontend/shared/components/Question/Actions/QuestionActions';
import { QuestionContext } from '@frontend/shared/components/Question/QuestionContext';
import { QuestionVerifiedBar } from '@frontend/shared/components/Question/QuestionVerifiedBar';
import { QuestionSorter } from '@frontend/shared/components/Question/Sorter/QuestionSorter';
import { useDirectoryUser } from '@frontend/shared/hooks/useDirectory';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

export function QuestionView({
  isExpanded,
  setEditMode,
  isCloseable,
}: {
  isExpanded?: boolean;
  setEditMode: (editMode: boolean) => void;
  isCloseable?: boolean;
}) {
  const question = useRequiredContext(QuestionContext);
  const user = useDirectoryUser(question.verified_by);

  return (
    <>
      <AccordionPrimitive.Header asChild>
        <AccordionPrimitive.Trigger asChild>
          <div className="group flex items-center transition-all duration-250 px-5 cursor-pointer hover:bg-[#F0F8F5] w-full">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.image} alt={user?.name} />
              <AvatarFallback>{user?.name?.charAt(0) || '?'}</AvatarFallback>
            </Avatar>

            <div className="flex w-full min-h-16 items-center">
              <div className="ml-4 py-5 flex-1">
                <div className="flex items-center mb-1">
                  <p className="font-medium text-[15px] leading-5 text-[#033426] tracking-[-0.01em] pr-2">
                    {question.title}
                  </p>
                </div>
              </div>
            </div>
            <div className="ml-auto pr-1 flex items-center justify-center">
              {isExpanded ? (
                <QuestionActions onEdit={() => setEditMode(true)} />
              ) : (
                <QuestionSorter />
              )}
            </div>

            <div className="flex-none w-8 h-8 flex items-center justify-center">
              {isCloseable ? (
                <IoClose className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              )}
            </div>
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>

      <AccordionPrimitive.Content className="p-0 mt-0 overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <Separator />
        <div className="py-6 px-8">
          <Answer
            question={question}
            editable={false}
            style={{ padding: 0, minHeight: 'auto' }}
          />
        </div>
        <QuestionVerifiedBar onEdit={() => setEditMode(true)} />
      </AccordionPrimitive.Content>
    </>
  );
}
